import { formatDistanceToNowStrict } from 'date-fns';
import { Loader2, Square } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { TaskStatusBadge } from '@/components/fleet/task-status-badge';
import { ErrorState } from '@/components/fleet/states';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/auth/auth-provider';
import { useProvisioningTask } from '@/hooks/use-provisioning-task';
import { useStopTask } from '@/hooks/use-stop-task';
import { useTaskLog } from '@/hooks/use-task-log';
import { isTerminalStatus, type ProvisioningTask } from '@/types/provisioning';

/** Elapsed wall-clock time of a task, ticking once a second while it runs. */
function ElapsedTime({ task }: { task: ProvisioningTask }): JSX.Element {
  const [, tick] = useState(0);
  const running = !isTerminalStatus(task.status);

  useEffect(() => {
    if (!running) return undefined;

    const id = setInterval(() => tick((n) => n + 1), 1_000);

    return () => clearInterval(id);
  }, [running]);

  const start = task.started_at ?? task.created_at;
  const end = task.finished_at ? new Date(task.finished_at) : new Date();
  const seconds = Math.max(0, Math.round((end.getTime() - new Date(start).getTime()) / 1000));
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;

  return (
    <span className="tabular-nums">
      {mm}m {String(ss).padStart(2, '0')}s
    </span>
  );
}

/** Live-tailing log viewer — auto-scrolls to the newest line. */
function TaskLogViewer({ task }: { task: ProvisioningTask }): JSX.Element {
  const { lines, streaming, error } = useTaskLog(task.task_id, task.status);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // `scrollIntoView` is unimplemented in jsdom — guard so tests don't crash.
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [lines.length]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Output log</h3>
        {streaming && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            live tail
          </span>
        )}
      </div>
      {error && <p className="text-xs text-destructive">Log unavailable: {error}</p>}
      <div className="max-h-72 overflow-auto rounded-md border bg-slate-950 p-3 font-mono text-xs text-slate-100">
        {lines.length === 0 ? (
          <p className="text-slate-500">No output yet.</p>
        ) : (
          lines.map((line, i) => (
            <div key={`${line.time}-${i}`} className="whitespace-pre-wrap break-words">
              {line.output}
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>
    </div>
  );
}

/**
 * The shared task-status surface — status badge, elapsed time, redacted args,
 * a Stop button (admin only, while running) and the live log tail. Polls
 * `GET /tasks/:id` every 2s until terminal. Reused by the fleet action panel
 * and the playbooks task drawer.
 */
export function TaskPanel({ taskId }: { taskId: number }): JSX.Element {
  const { isAdmin } = useAuth();
  const taskQuery = useProvisioningTask(taskId);
  const stopMutation = useStopTask();

  if (taskQuery.isLoading) {
    return (
      <div className="space-y-2" aria-label="Loading task" role="status">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (taskQuery.isError || !taskQuery.data) {
    return (
      <ErrorState
        title={`Couldn't load task #${taskId}.`}
        error={taskQuery.error}
        onRetry={() => void taskQuery.refetch()}
      />
    );
  }

  const task = taskQuery.data;
  const running = !isTerminalStatus(task.status);
  const argEntries = Object.entries(task.args ?? {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TaskStatusBadge status={task.status} />
          <span className="text-sm text-muted-foreground">
            elapsed <ElapsedTime task={task} />
          </span>
        </div>
        {running && isAdmin && (
          <Button
            variant="outline"
            size="sm"
            disabled={stopMutation.isPending}
            onClick={() => stopMutation.mutate(task.task_id)}
          >
            <Square className="h-3.5 w-3.5" aria-hidden />
            {stopMutation.isPending ? 'Stopping…' : 'Stop'}
          </Button>
        )}
      </div>

      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Playbook</dt>
        <dd>{task.template.name}</dd>
        <dt className="text-muted-foreground">Triggered by</dt>
        <dd className="font-mono text-xs">{task.actor}</dd>
        {task.reason && (
          <>
            <dt className="text-muted-foreground">Reason</dt>
            <dd>{task.reason}</dd>
          </>
        )}
        {argEntries.length > 0 && (
          <>
            <dt className="text-muted-foreground">Args</dt>
            <dd className="font-mono text-xs">
              {argEntries.map(([k, v]) => (
                <div key={k}>
                  {k}: {String(v)}
                </div>
              ))}
            </dd>
          </>
        )}
      </dl>

      {stopMutation.isError && (
        <p className="text-xs text-destructive">Stop failed: {stopMutation.error.message}</p>
      )}

      <TaskLogViewer task={task} />

      <p className="text-xs text-muted-foreground">
        Started {formatDistanceToNowStrict(new Date(task.started_at ?? task.created_at))} ago
      </p>
    </div>
  );
}
